import { when } from 'mobx';
import { inject } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { FRESHDESK, StorySettingsConstants as ui } from '../../Config/UIConstants';
import { CommonTabbedContainer, Tab } from '../../SharedComponents/CommonTabbedContainer';
import { CommonTextInput } from '../../SharedComponents/CommonTextInput';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { FullScreenModalContainer } from '../../SharedComponents/FullScreenModalContainer';
import { InformationIcon } from '../../SharedComponents/InformationIcon';
import { ModalActionButtonBar } from '../../SharedComponents/ModalActionButtonBar';
import { RemovalConfirmationPopup } from '../../SharedComponents/RemovalConfirmationPopup';
import { Routes, RouteFormats } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { CheckMarkCircleIcon, PadLock } from '../../images/images';
import { colors } from '../../styles/colors';
import { largeShadow } from '../../styles/styleObjects';
import { Url } from '../../util/Url';
import { parseIntWithAssert } from '../../util/string.utils';
import { NEW_FAMILY_ID, RETURN_URL } from '../FamiliesPage/AddFamilyPage';
import parseSearchString = Url.parseSearchString;

interface IState {
    storyName: string;
    activeTab: Tab;
    selectedFamilyIndex: number;
    confirmationPopup: boolean;
    warnShareStory: boolean;
    original: { storyName: string; selectedFamilyIndex: number };
    showExitWarning: boolean;
    cannotShareWarning: boolean;
    familyNameShared: string;
}

interface IProps {
    store: ApplicationStore;
}

type PropsIntersection = RouteComponentProps<any> & IProps;

@inject('store')
export class StorySettings extends React.Component<PropsIntersection, IState> {
    private readonly selectedFamilyRef: React.RefObject<HTMLDivElement>;

    public state: IState = {
        storyName: '',
        activeTab: Tab.LEFT,
        selectedFamilyIndex: -1,
        confirmationPopup: false,
        warnShareStory: false,
        original: { storyName: '', selectedFamilyIndex: -1 },
        showExitWarning: false,
        cannotShareWarning: false,
        familyNameShared: '',
    };

    constructor(props: PropsIntersection, context: unknown) {
        super(props, context);

        this.selectedFamilyRef = React.createRef();
    }

    public componentDidMount(): void {
        when(
            () => this.props.store.storyStore && this.props.store.storyStore.storiesAreLoaded,
            () => {

                const story = this.props.store.storyStore.getStoryByID(parseInt(this.props.match.params.id));

                let activeTab: Tab | undefined;
                let selectedFamilyIndex: number | undefined;
                let familyNameShared = '';
                let originalSelectedFamilyIndex = -1;

                if (story.sharedWithFamilyID) {
                    familyNameShared = this.props.store.userFamilyStore.getFamilyByID(story.sharedWithFamilyID).name;
                    originalSelectedFamilyIndex = story.sharedWithFamilyID;
                } else {
                    const parsedQueryString = parseSearchString(this.props.location.search);

                    const newFamilyId = parseIntWithAssert(parsedQueryString[NEW_FAMILY_ID]);
                    if (parsedQueryString[NEW_FAMILY_ID]) {
                        selectedFamilyIndex = this.props.store.userFamilyStore.userFamilies
                            .findIndex(family => family.id === newFamilyId);
                    }
                    if (selectedFamilyIndex && selectedFamilyIndex >= 0) {
                        activeTab = Tab.RIGHT;
                    } else {
                        activeTab = Tab.LEFT;
                    }
                }

                const nextState: Partial<IState> = {
                    storyName: story.title,
                    activeTab,
                    original: { storyName: story.title, selectedFamilyIndex: originalSelectedFamilyIndex },
                    familyNameShared,
                    selectedFamilyIndex,
                };

                this.setState(nextState as IState, () => {
                    const elSelectedFamily = this.selectedFamilyRef.current;
                    if (elSelectedFamily) {
                        elSelectedFamily.scrollIntoView();
                    }
                });
            },
        );
    }

    private backToStory(): void {
        // If we know the user came from the photo viewer, just go back, which preserves
        // the selected photo ID and scroll position. Otherwise, fall back to navitating to
        // the story being edited so as not to let the user get stuck.
        if (this.props.location.state && this.props.location.state.cameFromPhotoViewer) {
            this.props.history.goBack();
        } else {
            this.props.history.push(Routes.PHOTO_VIEWER.replace(':id', this.props.match.params.id));
        }
    }

    private async saveStorySettings(): Promise<void> {
        this.setState({ warnShareStory: false });
        this.props.store.storyStore.updateStoryTitle(parseInt(this.props.match.params.id), this.state.storyName);
        if (this.state.activeTab === Tab.RIGHT) {
            const families = this.props.store.userFamilyStore.userFamilies;
            const story = this.props.store.storyStore.getStoryByID(parseInt(this.props.match.params.id));
            const familyID = families[this.state.selectedFamilyIndex].id;
            await this.props.store.familyStore.addFamilyToCollection(story.id, familyID);
            story.isPrivate = false;
            story.sharedWithFamilyID = familyID;
        }
        this.backToStory();
    }

    private onClickSave(): void {
        if (this.state.activeTab === Tab.RIGHT && this.state.selectedFamilyIndex !== -1) {
            this.setState({ warnShareStory: true });
        } else {
            this.saveStorySettings();
        }
    }
    private deleteStory(): void {
        this.setState({ confirmationPopup: false });
        this.props.store.storyStore.deleteStory(parseInt(this.props.match.params.id));
        this.props.history.push(Routes.ALBUMS);
    }

    private onPressCreateNewFamily = (storyId: number): void => {
        this.props.history.push(Routes.ADD_FAMILY + '?' + RETURN_URL + '=' +
            encodeURIComponent(RouteFormats.storySettings(storyId)));
    }

    private onPressExit(): void {
        if (
            this.state.storyName !== this.state.original.storyName ||
            (this.state.selectedFamilyIndex !== -1 && this.state.activeTab === Tab.RIGHT)
        ) {
            this.setState({ showExitWarning: true });
            return;
        }
        this.backToStory();
    }

    private onPressTab(tab: Tab): void {
        const story = this.props.store.storyStore.getStoryByID(parseInt(this.props.match.params.id));

        if (tab === Tab.RIGHT && story.photosWithComments.length === 0) {
            this.setState({ cannotShareWarning: true });
            return;
        }

        this.setState({ activeTab: tab });
    }

    private renderLeftTabHeader = (): React.ReactNode => {
        return <React.Fragment>
            <InformationIcon
                link={FRESHDESK.KEEP_PRIVATE}
                style={{...styles.tabInfoIcon, ...styles.tabInfoIconLeft}}/>
            {ui.KEEP_PRIVATE_HEADER}
        </React.Fragment>;
    }

    private renderRightTabHeader = (): React.ReactNode => {
        return <React.Fragment>
            {ui.SHARE_HEADER}
            <InformationIcon
                link={FRESHDESK.SHARE_A_STORY}
                style={{...styles.tabInfoIcon, ...styles.tabInfoIconRight}}/>
        </React.Fragment>;
    }

    public render() {
        if (
            !this.props.store.userFamilyStore ||
            !this.props.store.userFamilyStore.familiesAreLoaded
        ) {
            return null;
        }

        const storyId = parseIntWithAssert(this.props.match.params.id as string);
        if (!storyId) {
            return null;
        }

        const families = this.props.store.userFamilyStore.userFamilies;

        return (
            <FullScreenModalContainer {...this.props} onPressExit={() => this.onPressExit()}>
                <FullContainer style={styles.container}>
                    <p style={styles.headerText}>{ui.HEADER}</p>
                    {this.state.familyNameShared !== '' && (
                        <p style={styles.sharedWith}>{ui.SHARED_WITH + this.state.familyNameShared}</p>
                    )}
                    <div style={styles.innerContainer}>
                        <CommonTextInput
                            header={ui.ALBUM_NAME}
                            onChangeText={text => this.setState({ storyName: text })}
                            value={this.state.storyName}
                            containerStyle={styles.input}
                            placeholder={ui.REQUIRED_PLACEHOLDER}
                            maxLength={70}
                        />
                        <div style={styles.heightBuffer} />
                        {this.state.activeTab ? (
                            <FullContainer>
                                {this.state.activeTab as any === Tab.RIGHT && (
                                    <p style={styles.shareWarning}>{ui.CANNOT_UNDO_SHARE}</p>
                                )}
                                <CommonTabbedContainer
                                    leftHeader={this.renderLeftTabHeader()}
                                    rightHeader={this.renderRightTabHeader()}
                                    activeTab={this.state.activeTab}
                                    onPressTab={(tab: Tab) => this.onPressTab(tab)}
                                    leftComponent={() => (
                                        <div style={styles.leftTabContainer}>
                                            <img src={PadLock} style={styles.lockImage} />
                                            <p style={styles.lockText}>{ui.NO_SHARE}</p>
                                        </div>
                                    )}
                                    rightComponent={() => (
                                        <div style={styles.rightTabContainer}>
                                            {families.map((family, index) => {
                                                let selectedFamilyRefObj: React.RefObject<HTMLDivElement> | undefined;
                                                const isSelectedFamily = this.state.selectedFamilyIndex === index;
                                                if (isSelectedFamily) {
                                                    selectedFamilyRefObj = this.selectedFamilyRef;
                                                }

                                                return (
                                                    <div
                                                        onClick={() => this.setState({ selectedFamilyIndex: index })}
                                                        key={family.id}
                                                        style={styles.familyRowContainer}
                                                        ref={selectedFamilyRefObj}
                                                    >
                                                        <p>{family.name}</p>
                                                        <div>
                                                            {isSelectedFamily ? (
                                                                <img
                                                                    src={CheckMarkCircleIcon}
                                                                    style={styles.familyCheckmark}
                                                                />
                                                            ) : (
                                                                <div style={styles.emptyCheckMark} />
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div
                                                style={styles.familyRowContainer}
                                                onClick={() => this.onPressCreateNewFamily(storyId)}
                                            >
                                                <p style={styles.createFamilyText}>{ui.CREATE_NEW_FAMILY}</p>
                                            </div>
                                        </div>
                                    )}
                                />
                            </FullContainer>
                        ) : (
                            <p style={styles.deleteWarning}>{ui.CANNOT_DELETE}</p>
                        )}
                    </div>
                </FullContainer>

                <ModalActionButtonBar
                    cancelOnClick={
                        this.state.activeTab ? () => this.setState({ confirmationPopup: true }) : undefined
                    }
                    style={styles.actionBar}
                    cancelText={this.state.activeTab ? ui.DELETE_ALBUM : undefined}
                    cancelInfoHref={FRESHDESK.DELETE_STORY}
                    confirmOnClick={() => this.onClickSave()}
                    confirmContent={ui.SAVE}
                    confirmStyle={styles.confirmButton}
                    altPadding={25}
                    confirmDisabled={
                        this.state.storyName === this.state.original.storyName &&
                        (this.state.activeTab === Tab.LEFT ||
                            this.state.original.selectedFamilyIndex === this.state.selectedFamilyIndex)
                    }
                />

                {this.state.confirmationPopup && (
                    <RemovalConfirmationPopup
                        headerText={ui.ARE_YOU_SURE_DELETE}
                        subHeaderText={ui.CANNOT_BE_UNDONE}
                        onClickCancel={() => this.setState({ confirmationPopup: false })}
                        onClickRemove={() => this.deleteStory()}
                    />
                )}
                {this.state.warnShareStory && (
                    <RemovalConfirmationPopup
                        headerText={ui.SHARE_WARNING}
                        subHeaderText={ui.CANNOT_BE_UNDONE}
                        removeText={ui.GO_BACK}
                        cancelText={ui.CONTINUE}
                        onClickCancel={() => this.saveStorySettings()}
                        onClickRemove={() => this.setState({ warnShareStory: false })}
                    />
                )}

                {this.state.showExitWarning && (
                    <RemovalConfirmationPopup
                        headerText={ui.ARE_YOU_SURE_CLOSE}
                        subHeaderText={ui.UNSAVED_CHANGES}
                        removeText={ui.GO_BACK}
                        cancelText={ui.LEAVE_PAGE}
                        onClickRemove={() => this.setState({ showExitWarning: false })}
                        onClickCancel={() =>
                            this.backToStory()
                        }
                    />
                )}

                {this.state.cannotShareWarning && (
                    <ExitableAlert
                        headerText={ui.CANNOT_SHARE}
                        onConfirmClick={() => this.setState({ cannotShareWarning: false })}
                        confirmText={`Ok`}
                    />
                )}
            </FullScreenModalContainer>
        );
    }
}

const styles: {[key: string]: React.CSSProperties} = {
    sharedWith: { fontSize: 25 },
    createFamilyText: { color: colors.ACTION_GREEN },
    actionBar: { justifyContent: 'flex-end' },
    confirmButton: { marginLeft: 20 },
    deleteWarning: {
        fontSize: 14,
        textAlign: 'center',
        width: '110%',
    },
    deleteButton: {
        backgroundColor: colors.CANCEL_TEXT,
    },
    heightBuffer: {
        height: 50,
    },
    deleteStoryContainer: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        marginTop: 20,
    },
    shareWarning: {
        width: '100%',
        textAlign: 'center',
        marginBottom: 15,
    },
    innerContainer: {
        width: 500,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    container: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
    },
    headerText: {
        fontSize: 25,
        color: '#423F4E',
        fontWeight: 900,
        marginTop: 70,
        height: 50,
    },
    input: {
        width: 400,
        height: 75,
        backgroundColor: 'white',
        marginTop: 40,
        boxShadow: largeShadow,
    },
    familyRowContainer: {
        height: 45,
        cursor: 'pointer',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        display: 'flex',
        paddingLeft: 15,
        paddingRight: 15,
        borderBottom: '1px solid rgb(245, 245, 245)',
    },
    familyCheckmark: {
        height: 20,
        width: 20,
        borderRadius: 20,
    },
    rightTabText: {
        color: 'black',
        paddingTop: 3,
        marginLeft: 8,
        textAlign: 'center',
        width: '100%',
    },
    rightTabContainer: {
        width: '100%',
        backgroundColor: 'white',
        alignItems: 'center',
        flexDirection: 'column',
        maxHeight: 285,
        overflowY: 'scroll',
    },
    lockText: {
        color: 'black',
        paddingTop: 3,
        marginLeft: 8,
    },
    lockImage: {
        marginLeft: 15,
        height: 15,
        width: 15,
        objectFit: 'contain',
    },
    leftTabContainer: {
        width: '100%',
        backgroundColor: 'white',
        height: 50,
        display: 'flex',
        alignItems: 'center',
    },
    tabInfoIcon: {
        position: 'absolute',
        height: '2em',
    },
    tabInfoIconLeft: {
        left: '-2.5em'
    },
    tabInfoIconRight: {
        right: '-2.5em'
    },
    emptyCheckMark: {
        height: 20,
        width: 20,
        borderRadius: 20,
        border: '1px solid #00000022',
    },
};



// WEBPACK FOOTER //
// ./src/Pages/StoriesPage/StorySettings.tsx